import React, { useState, useEffect } from 'react';
import { Server as ServerType } from '../../types';
import { api } from '../../utils/api';
import { Activity, XCircle, ShieldOff } from 'lucide-react';
import { 
     LineChart, Line, XAxis, YAxis, CartesianGrid, 
     Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

interface TrafficDataPoint {
     time: string;
     allowed: number;
     dropped: number;
}

// Interfaz actualizada para que coincida con ServerManagement.tsx
interface ServerTrafficModalProps {
     server: ServerType; 
     onClose: () => void;
     isClientPanel?: boolean; // Para que funcione en ambos paneles
     clientId?: string;      // Para la autenticación del cliente
}

// Este es el componente que me enviaste antes, ahora CORREGIDO
export const ServerTrafficModal: React.FC<ServerTrafficModalProps> = ({ server, onClose, isClientPanel = false, clientId }) => {
     const [data, setData] = useState<TrafficDataPoint[]>([]);
     const [loading, setLoading] = useState(true);
     const [error, setError] = useState('');
     const [timeframe, setTimeframe] = useState(3600);

     useEffect(() => {
         const fetchTraffic = async () => {
             setLoading(true);
             setError('');
             try {
                 const params = {
                     serverId: server.id,
                     ip: server.ip,
                     unit: 'mbps', // Confirmado por soporte
                     time: timeframe,
                     clientId: clientId || ''
                 };

                 let response;
                 if (isClientPanel) {
                     if (!clientId) {
                         throw new Error("Error: No se pudo verificar la identidad del cliente.");
                     }
                     response = await api.getClientServerTraffic(params);
                 } else {
                     response = await api.getServerTraffic(params);
                 }

                 if (!response.success) throw new Error(response.message || 'Error al cargar datos');

                 // Si 'traffic' es 'null', usamos un array vacío
                 const trafficData = response.data.traffic || []; 

                 // --- CORRECCIÓN DEL BUG .map() ---
                 const formattedData = trafficData.map((point: [number, number, number]) => ({
                     time: new Date(point[0] * 1000).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                     allowed: point[1],
                     dropped: point[2],
                 }));
                 setData(formattedData);

             } catch (err: any) {
                 setError(err.message || 'No se pudo cargar el análisis.');
             } finally {
                 setLoading(false);
             }
         };
         fetchTraffic();
     }, [server.id, server.ip, timeframe, isClientPanel, clientId]);

     return (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
             <div className="bg-white p-5 md:p-6 rounded-lg shadow-xl w-full max-w-3xl">
                 <div className="flex justify-between items-center mb-4">
                     <h3 className="text-lg font-semibold text-gray-800">Análisis de Ataques DDoS ({server.ip})</h3>
                     <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                         <XCircle className="w-5 h-5"/>
                     </button>
                 </div>
                 
                 <div className="mb-4">
                     <select value={timeframe} onChange={(e) => setTimeframe(Number(e.target.value))} className="p-2 border rounded-md bg-white">
                         <option value={3600}>Última hora</option>
                         <option value={21600}>Últimas 6 horas</option>
                         <option value={86400}>Últimas 24 horas</option>
                     </select>
                 </div>

                 {loading && (
                     <div className="flex flex-col justify-center items-center h-64 text-blue-500">
                         <Activity className="animate-heartbeat h-20 w-20" />
                         <p className="mt-4 text-lg font-semibold text-gray-700">Analizando tráfico...</p>
                     </div>
                 )}
                 {error && <div className="text-center text-red-500 p-8">{error}</div>}

                 {!loading && !error && data.length > 0 && (
                     <div style={{ width: '100%', height: 300 }}>
                         <ResponsiveContainer>
                             <LineChart data={data}>
                                 <CartesianGrid strokeDasharray="3 3" />
                                 <XAxis dataKey="time" />
                                 <YAxis />
                                 <Tooltip />
                                 <Legend />
                                 <Line type="monotone" dataKey="allowed" name="Tráfico Permitido (mbps)" stroke="#22c55e" />
                                 <Line type="monotone" dataKey="dropped" name="Tráfico Descartado (Ataque)" stroke="#ef4444" />
                             </LineChart>
                         </ResponsiveContainer>
                     </div>
                 )}
                 {!loading && !error && data.length === 0 && (
                     <div className="text-center text-gray-500 p-8 flex flex-col items-center h-64 justify-center">
                         <ShieldOff className="w-16 h-16 text-gray-400 mb-4" />
                     <h4 className="font-semibold text-xl text-gray-800">Todo en calma</h4>
                         <p className="text-gray-600">No se ha registrado tráfico de ataque en este período.</p>
                     </div>
                 )}
             </div>
         </div>
     );
};